import React, {
  createContext, useCallback, useContext, useState,
} from 'react';
import api from '../services/api';

interface SignInData {
    email: string;
    password: string;
}

interface AuthState {
    token: string;
    user: Record<string, unknown>;
}

interface AuthContextData {
    user?: Record<string, unknown>;
    signIn(credentials: SignInData): Promise<void>;
    singOut(): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AuthState>(() => {
    const token = localStorage.getItem('@EABApi:token');
    const user = localStorage.getItem('@EABApi:user');

    if (token && user) {
      return { token, user: JSON.parse(user) };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({ email, password }: SignInData) => {
    const response = await api.post('sessions', {
      email,
      password,
    });

    const { token, user } = response.data;

    localStorage.setItem('@EABApi:token', token);
    localStorage.setItem('@EABApi:user', JSON.stringify(user));

    setData({ token, user });
  }, []);

  const singOut = useCallback(() => {
    localStorage.removeItem('@EABApi:token');
    localStorage.removeItem('@EABApi:user');

    setData({} as AuthState);
  }, []);

  return (
    <AuthContext.Provider value={{ user: data.user, signIn, singOut }}>
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
